import { useMemo } from "react"
import { useQueries } from "@tanstack/react-query"
import { useFacilities } from "@/hooks/warehouse/useWarehouse"
import { WarehouseApi } from "@/lib/warehouse-api"
import { queryKeys } from "@/config/query-keys"
import type { FacilityStatistics, FacilityWithUnitTypes, UnitType } from "@/types/warehouse"

export type RentFacilityWithUnitTypes = FacilityWithUnitTypes & {
  occupiedUnitTypes: UnitType[]
}

function isPublicFacility(item: FacilityWithUnitTypes) {
  return item.facility.active && !item.facility.private
}

function getOnlineUnitTypes(unitTypes: UnitType[], statistics: FacilityStatistics) {
  const onlineUnitTypes = unitTypes.filter((unitType) => unitType.online)

  return {
    availableUnitTypes: onlineUnitTypes.filter(
      (unitType) => (statistics.unitTypes[unitType.code]?.online ?? 0) > 0,
    ),
    occupiedUnitTypes: onlineUnitTypes.filter(
      (unitType) => (statistics.unitTypes[unitType.code]?.online ?? 0) === 0,
    ),
  }
}

export function useRentFacilities() {
  const { data: facilitiesData, isLoading: isFacilitiesLoading } = useFacilities()

  const candidateFacilities = useMemo(
    () => facilitiesData?.items.filter(isPublicFacility) ?? [],
    [facilitiesData?.items],
  )

  const statisticsQueries = useQueries({
    queries: candidateFacilities.map((item) => ({
      queryKey: queryKeys.facilities.statistics(item.facility.code),
      queryFn: () => WarehouseApi.getPublicStatistics(item.facility.code),
      staleTime: Infinity,
      gcTime: 1000 * 60 * 5,
    })),
  })

  const isStatisticsLoading = statisticsQueries.some((query) => query.isPending)

  const availableFacilities = useMemo(() => {
    return candidateFacilities.reduce<RentFacilityWithUnitTypes[]>((result, item, index) => {
      const statistics = statisticsQueries[index]?.data
      if (!statistics || statistics.summary.online <= 0) {
        return result
      }

      const { availableUnitTypes, occupiedUnitTypes } = getOnlineUnitTypes(
        item.unitTypes,
        statistics,
      )

      result.push({
        facility: { ...item.facility, statistics },
        unitTypes: availableUnitTypes,
        occupiedUnitTypes,
      })
      return result
    }, [])
  }, [candidateFacilities, statisticsQueries])

  return {
    availableFacilities,
    isLoading: isFacilitiesLoading || isStatisticsLoading,
  }
}
